import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Download } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { libraryReportsApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { formatDate, orDash } from '@/utils/format';
import { BarRow, Field, Money, Stat, daysAgo, humanise, today } from './shared';

type ReportName =
  | 'circulation'
  | 'popular'
  | 'members'
  | 'inventory'
  | 'fines'
  | 'acquisitions';

const TABS: { key: ReportName; label: string; permission: string }[] = [
  { key: 'circulation', label: 'Circulation', permission: PERMISSIONS.LIBRARY_REPORT_VIEW },
  { key: 'popular', label: 'Popular books', permission: PERMISSIONS.LIBRARY_REPORT_VIEW },
  { key: 'members', label: 'Member activity', permission: PERMISSIONS.LIBRARY_REPORT_VIEW },
  { key: 'inventory', label: 'Inventory', permission: PERMISSIONS.LIBRARY_REPORT_VIEW },
  { key: 'fines', label: 'Fines', permission: PERMISSIONS.LIBRARY_FINE_VIEW },
  { key: 'acquisitions', label: 'Acquisitions', permission: PERMISSIONS.ACQUISITION_VIEW },
];

/**
 * §34's reports.
 *
 * <p>Each one states the range it covers, because a table of numbers with no
 * dates on it is the kind of thing that gets printed, filed, and later read as
 * though it described a different term.
 *
 * <p>The CSV comes from the same server method as the screen, so the file and
 * the page cannot disagree. The default range is the last thirty days rather
 * than all of history — a report whose default scans every loan a school ever
 * made is the full-table scan §44 rules out, and defaults are what people
 * actually use.
 */
export function LibraryReportsPage() {
  const { can } = useAuth();
  const available = TABS.filter((tab) => can(tab.permission));

  const [tab, setTab] = useState<ReportName>(available[0]?.key ?? 'circulation');
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());

  const range = { from, to };

  const circulation = useQuery({
    queryKey: queryKeys.library.report('circulation', range),
    queryFn: () => libraryReportsApi.circulation(from, to),
    enabled: tab === 'circulation',
  });

  const popular = useQuery({
    queryKey: queryKeys.library.report('popular', range),
    queryFn: () => libraryReportsApi.popularBooks(from, to),
    enabled: tab === 'popular',
  });

  const members = useQuery({
    queryKey: queryKeys.library.report('members', range),
    queryFn: () => libraryReportsApi.memberActivity(from, to),
    enabled: tab === 'members',
  });

  const inventory = useQuery({
    queryKey: queryKeys.library.report('inventory', undefined),
    queryFn: () => libraryReportsApi.inventory(),
    enabled: tab === 'inventory',
  });

  const fines = useQuery({
    queryKey: queryKeys.library.report('fines', range),
    queryFn: () => libraryReportsApi.fines(from, to),
    enabled: tab === 'fines',
  });

  const acquisitions = useQuery({
    queryKey: queryKeys.library.report('acquisitions', range),
    queryFn: () => libraryReportsApi.acquisitions(from, to),
    enabled: tab === 'acquisitions',
  });

  const busy =
    circulation.isFetching ||
    popular.isFetching ||
    members.isFetching ||
    inventory.isFetching ||
    fines.isFetching ||
    acquisitions.isFetching;

  function download() {
    switch (tab) {
      case 'circulation':
        void libraryReportsApi.circulationCsv();
        break;
      case 'popular':
        void libraryReportsApi.popularBooksCsv(from, to);
        break;
      case 'members':
        void libraryReportsApi.memberActivityCsv(from, to);
        break;
      case 'inventory':
        void libraryReportsApi.inventoryCsv();
        break;
      case 'fines':
        void libraryReportsApi.finesCsv(from, to);
        break;
      case 'acquisitions':
        void libraryReportsApi.acquisitionsCsv(from, to);
        break;
    }
  }

  const datedReport = tab !== 'inventory';

  return (
    <div>
      <PageHeader
        title="Library reports"
        description="Totals the database computed, over a range you choose."
        icon={<ClipboardList className="h-5 w-5" aria-hidden="true" />}
        action={
          <Button variant="secondary" onClick={download}>
            <Download className="h-4 w-4" aria-hidden="true" />
            CSV
          </Button>
        }
      />

      <div className="mb-3 flex flex-wrap items-end gap-2">
        {available.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            aria-pressed={tab === entry.key}
            className={`border px-3 py-2 text-sm font-semibold ${
              tab === entry.key
                ? 'border-accent bg-accent-soft text-accent-soft-fg'
                : 'border-line bg-bg text-muted'
            }`}
          >
            {entry.label}
          </button>
        ))}

        {datedReport && (
          <span className="ml-auto flex items-end gap-2">
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </span>
        )}
      </div>

      {busy ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          {tab === 'circulation' && circulation.data !== undefined && (
            <>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <Stat label="Issued" value={circulation.data.issued} />
                <Stat label="Returned" value={circulation.data.returned} />
                <Stat label="Renewed" value={circulation.data.renewed} />
                <Stat label="Still out" value={circulation.data.stillOut} />
                <Stat
                  label="Overdue now"
                  value={circulation.data.overdueNow}
                  tone={circulation.data.overdueNow > 0 ? 'danger' : 'neutral'}
                />
              </div>
              <Card className="mt-4 p-4">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  Issues per day
                </h2>
                {circulation.data.perDay.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted">
                    Nothing was borrowed in this range.
                  </p>
                ) : (
                  <div className="mt-2">
                    {circulation.data.perDay.map((day) => (
                      <BarRow
                        key={day.date}
                        label={formatDate(day.date)}
                        value={day.count}
                        max={Math.max(1, ...circulation.data.perDay.map((row) => row.count))}
                      />
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}

          {tab === 'popular' && popular.data !== undefined && (
            <Card className="p-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Most borrowed, {formatDate(popular.data.from)} to {formatDate(popular.data.to)}
              </h2>
              {popular.data.titles.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">
                  Not enough borrowing in this range to rank anything.
                </p>
              ) : (
                <div className="mt-2">
                  {popular.data.titles.map((title) => (
                    <BarRow
                      key={title.bookId}
                      label={`${title.title}${title.author === undefined ? '' : ` — ${title.author}`}`}
                      value={title.borrowings}
                      max={Math.max(1, ...popular.data.titles.map((row) => row.borrowings))}
                      suffix={`${title.borrowings}×`}
                    />
                  ))}
                </div>
              )}
            </Card>
          )}

          {tab === 'members' && members.data !== undefined && (
            <Card className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-[11px] uppercase tracking-[0.04em] text-muted">
                  <tr>
                    <th className="px-5 py-2">Member</th>
                    <th className="px-5 py-2">Type</th>
                    <th className="px-5 py-2 text-right">Borrowings</th>
                    <th className="px-5 py-2 text-right">Out now</th>
                    <th className="px-5 py-2 text-right">Overdue</th>
                    <th className="px-5 py-2 text-right">Fines</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {members.data.rows.map((row) => (
                    <tr key={row.memberId}>
                      <td className="px-5 py-2">
                        <span className="block text-ink">{orDash(row.name)}</span>
                        <span className="block font-mono text-[11px] text-subtle">
                          {orDash(row.membershipNumber)}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-muted">
                        {row.memberType === undefined ? '—' : humanise(row.memberType)}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">{row.borrowings}</td>
                      <td className="px-5 py-2 text-right tabular-nums">{row.currentlyOut}</td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {row.overdue > 0 ? (
                          <span className="text-[color:var(--sms-danger)]">{row.overdue}</span>
                        ) : (
                          row.overdue
                        )}
                      </td>
                      <td className="px-5 py-2 text-right">
                        <Money value={row.outstandingFines} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {members.data.rows.length === 0 && (
                <p className="py-8 text-center text-sm text-muted">
                  Nobody borrowed anything in this range.
                </p>
              )}
            </Card>
          )}

          {tab === 'inventory' && inventory.data !== undefined && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Stat label="Titles" value={inventory.data.totalTitles} />
                <Stat
                  label="Copies"
                  value={inventory.data.totalCopies}
                  hint={`As at ${formatDate(inventory.data.asOf)}`}
                />
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <Card className="p-4">
                  <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                    Copies by status
                  </h2>
                  <dl className="mt-2">
                    {inventory.data.byStatus.map((row) => (
                      <Field key={row.status} label={humanise(row.status)}>
                        {row.copies}
                      </Field>
                    ))}
                  </dl>
                </Card>
                <Card className="p-4">
                  <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                    Titles by category
                  </h2>
                  <div className="mt-2">
                    {inventory.data.byCategory.map((row) => (
                      <BarRow
                        key={row.category}
                        label={row.category}
                        value={row.titles}
                        max={Math.max(1, ...inventory.data.byCategory.map((c) => c.titles))}
                      />
                    ))}
                  </div>
                </Card>
              </div>
            </>
          )}

          {tab === 'fines' && fines.data !== undefined && (
            <>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <Stat
                  label="Raised"
                  value={<Money value={fines.data.raised} />}
                  hint={`${fines.data.count} fine${fines.data.count === 1 ? '' : 's'}`}
                />
                <Stat label="Collected" value={<Money value={fines.data.collected} />} tone="success" />
                <Stat
                  label="Waived"
                  value={<Money value={fines.data.waived} />}
                  tone="info"
                  hint="What the school chose not to pursue"
                />
                <Stat
                  label="Still owed"
                  value={<Money value={fines.data.outstanding} />}
                  tone={fines.data.outstanding > 0 ? 'warning' : 'neutral'}
                />
              </div>
              <Card className="mt-4 p-4">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  Why people were fined
                </h2>
                {fines.data.byType.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted">
                    Nobody was fined in this range.
                  </p>
                ) : (
                  <div className="mt-2">
                    {fines.data.byType.map((row) => (
                      <BarRow
                        key={row.type}
                        label={`${humanise(row.type)} (${row.count})`}
                        value={row.raised}
                        max={Math.max(1, ...fines.data.byType.map((entry) => entry.raised))}
                        suffix={<Money value={row.raised} />}
                      />
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}

          {tab === 'acquisitions' && acquisitions.data !== undefined && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <Stat label="Orders received" value={acquisitions.data.orders} />
                <Stat label="Spent" value={<Money value={acquisitions.data.totalSpend} />} />
                <Stat label="Copies added" value={acquisitions.data.copiesReceived} />
              </div>
              <Card className="mt-4 p-4">
                <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                  Spend by supplier
                </h2>
                <p className="mt-0.5 text-xs text-muted">
                  Part-received orders count too: those copies are on the shelves and that money
                  has been spent.
                </p>
                {acquisitions.data.bySupplier.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted">
                    Nothing was received in this range.
                  </p>
                ) : (
                  <div className="mt-2">
                    {acquisitions.data.bySupplier.map((row) => (
                      <BarRow
                        key={row.supplier}
                        label={`${row.supplier} (${row.copies} cop${row.copies === 1 ? 'y' : 'ies'})`}
                        value={row.spend}
                        max={Math.max(1, ...acquisitions.data.bySupplier.map((s) => s.spend))}
                        suffix={<Money value={row.spend} />}
                      />
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
